import React, { useRef, useMemo, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import Chart from "react-apexcharts";
import { styled } from "@mui/material/styles";
import { PrimaryTooltip } from "../../../Components/StyledComponent";

const SCROLLBAR_WIDTH = '8px';

const ChartContainer = styled(Box)({
    width: '100%',
    display: 'flex',
    gap: '16px',
    maxHeight: '400px',
    overflowY: 'scroll', // Always show scrollbar to match fixed padding below
    overflowX: 'hidden',
    '&::-webkit-scrollbar': {
        width: SCROLLBAR_WIDTH,
    },
    '&::-webkit-scrollbar-track': {
        backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#D1D1D1',
        borderRadius: '4px',
    }
});

const LabelsContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    height: '100%'
});

const ChartWrapper = styled(Box)({
    flex: 1,
    minWidth: 0
});


const BreakdownChart = ({ categories = [], series = [], height, loading, options: parentOptions = {}, xAxisTickFormatter, minVisibilityHeight = 200, labelWidth = '180px', maxContainerHeight = '400px', tooltipOverflow = false }) => {
    const hoveredIndexRef = useRef(null);
    // Remove internal chartData

    const { xaxisMax, ticks } = useMemo(() => {
        if (!series || series.length === 0) return { xaxisMax: 100, ticks: [0, 33, 66, 100] };

        // Check if stacked
        const isStacked = parentOptions?.chart?.stacked;

        // Find max value from data
        let dataMax = 0;
        if (isStacked) {
            // Sum values at each index
            const dataLength = series[0]?.data?.length || 0;
            for (let i = 0; i < dataLength; i++) {
                let sum = 0;
                series.forEach(s => {
                    sum += (s.data[i] || 0);
                });
                if (sum > dataMax) dataMax = sum;
            }
        } else {
            // Max of all individual values
            series.forEach(s => {
                const sMax = Math.max(...(s.data || []));
                if (sMax > dataMax) dataMax = sMax;
            });
        }

        if (dataMax === 0) return { xaxisMax: 100, ticks: [0, 25, 50, 75, 100] };

        // Calculate Nice Scale
        // We want roughly 3-5 ticks
        const targetTickCount = 4;
        const roughStep = dataMax / (targetTickCount - 1);

        // Calculate magnitude and normalized step
        const calculateNiceStep = (value) => {
            const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
            const normalized = value / magnitude;

            let niceNormalized;
            if (normalized <= 1) niceNormalized = 1;
            else if (normalized <= 2) niceNormalized = 2;
            else if (normalized <= 5) niceNormalized = 5;
            else niceNormalized = 10;

            return niceNormalized * magnitude;
        };

        let niceStep = calculateNiceStep(roughStep);

        // Special case for time (if formatting is involved, ideally we'd know unit, but pure numbers:
        // 1h=3600s. niceStep of 2000s is weird (33m). 1800s (30m) or 3600s (1h) is better.
        // But since we are generic, 1, 2, 5 * 10^n is standard "nice number" algo.
        // It produces 1000, 2000, 5000 etc. or 0.1, 0.2, 0.5.
        // For seconds:
        // 3600 -> step ~1200. Nice step 1000 (16.6m). 2000 (33m).
        // Standard nice scale might produce 1000s (16m 40s) which is awkward for time.
        // However, without knowing the unit type explicitly (seconds vs counts), generic nice scale is safest alignment-wise.
        // User complaint is "tick mark ... way off".
        // Let's stick to standard nice scale (1, 2, 5) which mathematically guarantees alignment.

        // Ensure step is at least 1
        niceStep = Math.max(1, niceStep);

        // Calculate max based on nice step
        const niceMax = Math.ceil(dataMax / niceStep) * niceStep;

        // Generate ticks
        const newTicks = [];
        for (let i = 0; i <= niceMax; i += niceStep) {
            newTicks.push(i);
        }
        // Handle floating point errors in loop
        // Better: count steps
        const stepCount = Math.round(niceMax / niceStep);
        const generatedTicks = Array.from({ length: stepCount + 1 }, (_, i) => i * niceStep);

        // Respect parent's buffer-inclusive max when it's larger than niceMax.
        // Parents compute xAxisMax with extra room for data label text; overriding
        // with niceMax (which barely clears the tallest bar) clips labels for long
        // bars and leaves the wrong amount of space for short bars.
        const parentMax = parentOptions?.xaxis?.max ?? 0;
        const effectiveMax = Math.max(niceMax, parentMax);

        return { xaxisMax: effectiveMax, ticks: generatedTicks };
    }, [series, parentOptions]);

    const fixedBarHeight = 16; // Fixed height per bar
    const barGap = 30;
    const numBars = categories?.length || 0;
    const totalPadding = 0; // Top and bottom padding
    const labelOffset = 0; // Extra padding to push labels down
    const labelHeight = fixedBarHeight + barGap;

    // Minimum height to ensure tooltips are not clipped by container
    // const MIN_CHART_VISIBILITY_HEIGHT = 375; // Removed constant, using prop

    // Calculate content height based on bars
    const contentHeight = numBars * labelHeight + totalPadding;

    // Use dynamic height if provided, else calc based on bars
    let chartHeight = height || contentHeight;
    let extraBottomPadding = 0;

    // If using auto-height and content is too small, add padding to ensure visibility
    if (!height && contentHeight < minVisibilityHeight) {
        extraBottomPadding = minVisibilityHeight - contentHeight;
        chartHeight = minVisibilityHeight;
    }

    const maxHeightPx = parseInt(maxContainerHeight) || 400;
    const needsScroll = chartHeight > maxHeightPx;
    const containerOverflowY = (tooltipOverflow && !needsScroll) ? 'visible' : 'scroll';
    const containerOverflowX = (tooltipOverflow && !needsScroll) ? 'visible' : 'hidden';

    const addHoverBackground = useCallback((index, chartContext) => {
        // Clear all existing highlights first
        // const chart = chartContext?.chart; // This line was removed as per instruction 2

        // 1. Clear Chart Background
        if (chartContext) {
            const existingBgs = chartContext.el.querySelectorAll('.hover-row-bg');
            existingBgs.forEach(bg => bg.remove());
        }

        // 2. Clear Label Backgrounds
        // We use a specific class or check all labels
        const allLabels = document.querySelectorAll('[id^="custom-label-"]');
        allLabels.forEach(label => {
            label.style.backgroundColor = 'transparent';
        });

        // If index is null (or invalid), we are just clearing
        if (index === null || index === undefined || index < 0) return;

        // Apply New Highlights

        // 1. Chart Background
        if (chartContext) {
            const innerChart = chartContext.el.querySelector('.apexcharts-inner');
            if (innerChart) {
                const chartWidth = innerChart.getBBox().width;
                // Calculate Y position
                // Bar is centered in the slot.
                // Slot height = labelHeight. Bar height = fixedBarHeight.
                // We want highlight to be BarHeight + 8px (4px top + 4px bottom padding)
                const hoverHeight = fixedBarHeight + 8;
                const offset = (labelHeight - hoverHeight) / 2;
                const yPos = index * labelHeight + offset + totalPadding / 2;

                const svgNS = "http://www.w3.org/2000/svg";
                const rect = document.createElementNS(svgNS, 'rect');
                rect.setAttribute('class', 'hover-row-bg');
                rect.setAttribute('x', '0');
                rect.setAttribute('y', yPos);
                rect.setAttribute('width', chartWidth);
                rect.setAttribute('height', hoverHeight);
                rect.setAttribute('fill', '#E9E9E9');

                innerChart.insertBefore(rect, innerChart.firstChild);
            }
        }

    }, [labelHeight, totalPadding]);

    // Cleanup helper not strictly needed separate if addHover handles null, but good for clarity
    const removeHoverBackground = useCallback(() => {
        addHoverBackground(null, null);
    }, [addHoverBackground]);

    const options = useMemo(() => ({
        ...parentOptions, // Merge parent options
        chart: {
            ...parentOptions?.chart,
            type: 'bar',
            height: chartHeight,
            stacked: parentOptions?.chart?.stacked, // Preserve stacked
            sparkline: {
                enabled: true
            },
            parentHeightOffset: 0,
            toolbar: {
                show: false
            },
            events: {
                ...parentOptions?.chart?.events, // Preserve other events if any
                mouseMove: (event, chartContext, config) => {
                    const dataPointIndex = config.dataPointIndex;
                    if (dataPointIndex >= 0 && dataPointIndex !== hoveredIndexRef.current) {
                        hoveredIndexRef.current = dataPointIndex;
                        addHoverBackground(dataPointIndex, chartContext);
                    }
                     // Call parent handler if needed? Maybe not for now since we own hover.
                },
                mouseLeave: () => {
                    hoveredIndexRef.current = null;
                    removeHoverBackground();
                },
                dataPointSelection: parentOptions?.chart?.events?.dataPointSelection
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                borderRadiusApplication: 'end',
                horizontal: true, // Always horizontal for this component
                barHeight: '16px',
                distributed: false,
                ...parentOptions?.plotOptions?.bar // Override if parent wants
            }
        },
        dataLabels: {
            enabled: false, // Default false usually
             ...parentOptions?.dataLabels
        },
        grid: {
            show: true,
            padding: {
                top: 0,
                bottom: extraBottomPadding,
                left: 0,
                right: 0
            },
            xaxis: {
                lines: {
                    show: false
                }
            },
            yaxis: {
                lines: {
                    show: false
                }
            }
        },
        xaxis: {
             ...parentOptions?.xaxis,
            categories: categories, // Use prop
            min: 0,
            max: xaxisMax, // Use calc or parent
            axisBorder: {
                show: false
            },
            axisTicks: {
                show: false
            },
            labels: {
                show: false
            }
        },
        yaxis: {
            show: false,
             ...parentOptions?.yaxis
        },
        tooltip: {
            ...parentOptions?.tooltip,
            enabled: true,
            shared: true,
            intersect: false,
            followCursor: true,
        }
    }), [categories, chartHeight, addHoverBackground, removeHoverBackground, xaxisMax, parentOptions, extraBottomPadding]);

    const chartSeries = useMemo(() => series, [series]);

    return (
        <Box sx={{ width: '100%', minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
            <ChartContainer sx={{ maxHeight: maxContainerHeight, overflowY: containerOverflowY, overflowX: containerOverflowX }}>
                <LabelsContainer
                    sx={{
                        width: labelWidth,
                        paddingTop: `${totalPadding / 2 + labelOffset}px`,
                        paddingBottom: `${totalPadding / 2}px`
                    }}
                    onMouseLeave={() => {
                        hoveredIndexRef.current = null;
                        removeHoverBackground();
                    }}
                >
                    {categories.map((category, index) => (
                        <Box
                            key={index}
                            // Outer box detects hover over full area
                            onMouseEnter={() => {
                                if (hoveredIndexRef.current !== index) {
                                    hoveredIndexRef.current = index;
                                    addHoverBackground(index);
                                }
                            }}
                            sx={{
                                height: `${labelHeight}px`,
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer',
                            }}
                        >
                            {/* Inner box receives the highlight background */}
                            <Box
                                id={`custom-label-${index}`}
                                sx={{
                                    height: `${fixedBarHeight + 8}px`, // 16 + 8 = 24px
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: 'transparent',
                                    borderRadius: '4px',
                                    transition: 'background-color 0.1s ease'
                                }}
                            >
                                <PrimaryTooltip title={category} placement="bottom">
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: '14px',
                                            lineHeight: '16px',
                                            color: '#494949',
                                            textAlign: 'left',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            width: '100%',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {category}
                                    </Typography>
                                </PrimaryTooltip>
                            </Box>
                        </Box>
                    ))}
                </LabelsContainer>
                <ChartWrapper>
                    <Chart
                        options={options}
                        series={chartSeries}
                        type="bar"
                        height={chartHeight}
                    />
                </ChartWrapper>
            </ChartContainer>

            {/* Custom Fixed X-Axis */}
            <Box sx={{ display: 'flex', gap: '16px', width: '100%', marginTop: '8px', paddingRight: containerOverflowY === 'scroll' ? SCROLLBAR_WIDTH : 0 }}>
                {/* Spacer matching LabelsContainer width */}
                <Box sx={{ width: labelWidth, flexShrink: 0 }} />

                {/* Axis Area matching ChartWrapper flex */}
                <Box sx={{ flex: 1, position: 'relative', height: '30px', borderTop: '1px solid #E0E0E0' }}>
                    {ticks.map((tick, index) => (
                        <Box
                            key={tick}
                            sx={{
                                position: 'absolute',
                                left: `${(tick / xaxisMax) * 100}%`,
                                transform: 'translateX(-50%)',
                                top: '4px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center'
                            }}
                        >
                            {/* Tick Mark */}
                            <Typography variant="caption" sx={{ fontSize: '12px', lineHeight: '24px', color: '#8F8F8F', whiteSpace: 'nowrap' }}>
                                {xAxisTickFormatter ? xAxisTickFormatter(tick) : tick}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

export default BreakdownChart;
